import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Radio, Send, Clock, PlayCircle, MessageSquare, Sparkles, BellRing } from "lucide-react";
import { toast } from "sonner";
import { Section, Container, Eyebrow, DisplayH1, Reveal } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function useCountdown(target) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!target) return null;
  const diff = Math.max(0, target.getTime() - now.getTime());
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds, ended: diff === 0 };
}

export default function Livestream() {
  const { user } = useAuth();
  const [state, setState] = useState({ next: null, live: null, latest_replay: null });
  const [text, setText] = useState("");
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [past, setPast] = useState([]);
  const listRef = useRef(null);
  const sinceRef = useRef(null);

  useEffect(() => {
    api.get("/livestream/next").then((r) => setState(r.data));
    api.get("/livestream/list").then((r) => setPast(r.data.livestreams || []));
  }, []);

  const stream = state.live || state.next || state.latest_replay;
  const targetTime = state.next?.scheduled_at ? new Date(state.next.scheduled_at) : null;
  const cd = useCountdown(state.live ? null : targetTime);

  // Chat polling
  useEffect(() => {
    if (!stream) return;
    let cancel = false;
    const poll = async () => {
      try {
        const r = await api.get(`/livestream/${stream.stream_id}/chat`, { params: sinceRef.current ? { since: sinceRef.current } : {} });
        if (cancel) return;
        const incoming = r.data.messages || [];
        if (incoming.length) {
          setMessages((m) => [...m, ...incoming]);
          sinceRef.current = incoming[incoming.length - 1].created_at;
        }
      } catch {}
    };
    poll();
    const t = setInterval(poll, 4000);
    return () => { cancel = true; clearInterval(t); };
  }, [stream?.stream_id]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to join the chat."); return; }
    if (!text.trim() || !stream) return;
    setSending(true);
    try {
      const r = await api.post(`/livestream/${stream.stream_id}/chat`, { text: text.trim() });
      setMessages((m) => [...m, r.data]);
      sinceRef.current = r.data.created_at;
      setText("");
    } catch { toast.error("Message not sent."); }
    finally { setSending(false); }
  };

  return (
    <>
      <Section testid="livestream-hero" className="pt-32 pb-10">
        <Container>
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${state.live ? "bg-red-500 animate-pulse" : "bg-[color:var(--wk-gold)]"}`} />
            <Eyebrow>{state.live ? "LIVE NOW" : "The Founder's Circle · Monthly"}</Eyebrow>
          </div>
          <DisplayH1 className="mt-4 max-w-4xl">
            {state.live ? "We are live." : "Feel the pack in real time."}
          </DisplayH1>
          <p className="mt-6 max-w-2xl text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">
            Every month, Toriano opens the doors to the founder's studio. Members ask
            questions, the pack chats live, and replays are archived here forever.
          </p>
        </Container>
      </Section>

      {/* Player + chat */}
      <Section testid="livestream-main" className="pb-16">
        <Container>
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              <div className="relative overflow-hidden rounded-3xl aspect-video card-lux">
                {(state.live?.embed_url) ? (
                  <iframe
                    src={state.live.embed_url}
                    title={state.live.title}
                    className="absolute inset-0 w-full h-full"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                  />
                ) : (state.latest_replay?.embed_url || state.latest_replay?.replay_url) ? (
                  <div className="absolute inset-0">
                    <img src={state.latest_replay?.poster_url} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 cinema-overlay" />
                    <a
                      href={state.latest_replay.embed_url || state.latest_replay.replay_url}
                      target="_blank" rel="noreferrer"
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <div className="w-20 h-20 rounded-full bg-[color:var(--wk-gold)] text-black flex items-center justify-center hover:scale-105 transition-transform">
                        <PlayCircle size={36} />
                      </div>
                    </a>
                  </div>
                ) : stream?.poster_url ? (
                  <div className="absolute inset-0">
                    <img src={stream.poster_url} alt="" className="w-full h-full object-cover kenburns" />
                    <div className="absolute inset-0 cinema-overlay" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="glass-strong rounded-2xl p-8 md:p-10 text-center max-w-lg">
                        <Radio className="text-[color:var(--wk-gold)] mx-auto" />
                        <div className="mt-4 overline">Next stream</div>
                        <div className="mt-2 font-serif-display text-2xl md:text-3xl leading-tight">{stream.title}</div>
                        {cd && (
                          <div className="mt-6 grid grid-cols-4 gap-3 max-w-md mx-auto" data-testid="livestream-countdown">
                            {[
                              { label: "Days", v: cd.days },
                              { label: "Hours", v: cd.hours },
                              { label: "Min", v: cd.minutes },
                              { label: "Sec", v: cd.seconds },
                            ].map((u) => (
                              <div key={u.label} className="hairline rounded-xl p-3">
                                <div className="font-serif-display text-3xl md:text-4xl text-[color:var(--wk-gold)] tabular-nums">{String(u.v).padStart(2, "0")}</div>
                                <div className="overline text-[9px] mt-1">{u.label}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="mt-5 text-xs text-[color:var(--wk-bone-dim)]">
                          {stream.scheduled_at && new Date(stream.scheduled_at).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" })}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-[color:var(--wk-bg-2)] text-[color:var(--wk-bone-dim)]">No stream scheduled yet.</div>
                )}
              </div>
              {stream?.description && (
                <p className="mt-6 text-[color:var(--wk-bone-dim)] leading-relaxed max-w-3xl">{stream.description}</p>
              )}
              <NotifyMeWidget stream_id={stream?.stream_id} defaultEmail={user?.email} />
            </div>

            {/* Chat */}
            <div className="lg:col-span-4">
              <div className="card-lux h-[560px] flex flex-col overflow-hidden">
                <div className="p-4 border-b border-white/10 flex items-center gap-2">
                  <MessageSquare size={16} className="text-[color:var(--wk-gold)]" />
                  <div className="text-sm">Pack Chat</div>
                  <span className="ml-auto text-xs text-[color:var(--wk-bone-dim)]">{messages.length}</span>
                </div>
                <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="livestream-chat">
                  {messages.length === 0 && (
                    <div className="text-sm text-[color:var(--wk-bone-dim)] text-center pt-16">
                      Chat opens 30 minutes before the stream. Say hi, meet the pack.
                    </div>
                  )}
                  {messages.map((m) => (
                    <motion.div
                      key={m.message_id}
                      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                      className="text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <span className={`font-serif-display ${m.is_admin ? "text-[color:var(--wk-gold)]" : "text-[color:var(--wk-bone)]"}`}>
                          {m.user_name}{m.is_admin ? " · Founder" : ""}
                        </span>
                        <span className="text-[10px] text-[color:var(--wk-bone-dim)]">
                          {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <div className="text-[color:var(--wk-bone-dim)] mt-0.5">{m.text}</div>
                    </motion.div>
                  ))}
                </div>
                <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
                  <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={user ? "Say hi to the pack…" : "Sign in to chat"}
                    disabled={!user}
                    data-testid="livestream-chat-input"
                    className="flex-1 bg-[#0f0f0f] border border-white/10 focus:border-[color:var(--wk-gold)] outline-none rounded-full px-4 py-2 text-sm text-[color:var(--wk-bone)] placeholder:text-[color:var(--wk-muted)] disabled:opacity-50"
                  />
                  <button disabled={sending || !user || !text.trim()} className="btn-gold h-10 w-10 p-0 rounded-full disabled:opacity-50" data-testid="livestream-chat-send" aria-label="Send">
                    <Send size={14} />
                  </button>
                </form>
              </div>
              {!user && (
                <Link to="/login" className="btn-ghost w-full mt-4 justify-center">Sign in to chat</Link>
              )}
            </div>
          </div>
        </Container>
      </Section>

      {/* Past streams / replays */}
      <Section testid="livestream-past" className="pb-32">
        <Container>
          <div className="flex items-end justify-between mb-8">
            <div>
              <Eyebrow>Replays</Eyebrow>
              <div className="mt-2 font-serif-display text-3xl">Every past stream, forever.</div>
            </div>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {past.length === 0 && (
              <div className="col-span-full card-lux p-8 text-[color:var(--wk-bone-dim)] flex items-center gap-3">
                <Sparkles size={16} className="text-[color:var(--wk-gold)]" />
                Replays post here after each stream. The very first Founder's Circle is coming up.
              </div>
            )}
            {past.map((s) => (
              <Reveal key={s.stream_id}>
                <div className="card-lux overflow-hidden">
                  <div className="relative aspect-video">
                    <img src={s.poster_url || "https://customer-assets-v7afamib.emergentagent.net/job_packleader-hub/artifacts/0f7iuk2g_Trailblazer.jpeg"} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 cinema-overlay" />
                    <div className="absolute top-3 left-3 overline text-[10px] px-2 py-1 rounded-full glass border-white/20">
                      {s.is_live ? "LIVE" : s.replay_url ? "REPLAY" : "SCHEDULED"}
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="font-serif-display text-xl leading-tight">{s.title}</div>
                    <div className="mt-2 flex items-center gap-2 text-xs text-[color:var(--wk-bone-dim)]">
                      <Clock size={12} /> {new Date(s.scheduled_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>
    </>
  );
}


function NotifyMeWidget({ stream_id, defaultEmail }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [subbed, setSubbed] = useState(false);
  const [browserOn, setBrowserOn] = useState(false);

  useEffect(() => {
    setEmail((e) => e || defaultEmail || "");
    if (typeof window !== "undefined" && "Notification" in window) {
      setBrowserOn(Notification.permission === "granted");
    }
  }, [defaultEmail]);

  const requestBrowser = async () => {
    if (!("Notification" in window)) { toast.error("Notifications aren't supported here."); return; }
    try {
      const p = await Notification.requestPermission();
      if (p === "granted") { setBrowserOn(true); toast.success("We'll ping you the moment Toriano goes live."); }
      else toast.error("Permission was declined.");
    } catch { toast.error("Couldn't set up notifications."); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/livestream/subscribe", { email, phone: phone || null, stream_id });
      setSubbed(true);
      toast.success("You're on the list. We'll notify you before the next stream.");
    } catch { toast.error("Could not subscribe."); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6 card-lux p-6 md:p-8 max-w-2xl" data-testid="notify-widget">
      <div className="flex items-center gap-3">
        <BellRing className="text-[color:var(--wk-gold)]" size={20} />
        <div>
          <div className="font-serif-display text-xl">Never miss a stream.</div>
          <div className="text-xs text-[color:var(--wk-bone-dim)]">
            Get a live in-page + browser alert the moment Toriano is on air.
            {browserOn ? " Browser alerts: on." : ""}
          </div>
        </div>
        {!browserOn && (
          <button onClick={requestBrowser} data-testid="notify-browser" className="ml-auto btn-ghost h-9 text-xs">
            Enable browser alerts
          </button>
        )}
      </div>
      {!subbed ? (
        <form onSubmit={submit} className="mt-4 flex flex-col md:flex-row gap-3">
          <input
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            data-testid="notify-email"
            className="flex-1 bg-transparent border-b border-white/20 focus:border-[color:var(--wk-gold)] outline-none py-2 text-sm"
          />
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone (optional)"
            data-testid="notify-phone"
            className="flex-1 bg-transparent border-b border-white/20 focus:border-[color:var(--wk-gold)] outline-none py-2 text-sm"
          />
          <button disabled={busy} className="btn-gold h-10 text-xs" data-testid="notify-submit">
            {busy ? "Adding you…" : "Notify me"}
          </button>
        </form>
      ) : (
        <div className="mt-4 text-sm text-[color:var(--wk-gold)]">
          You're subscribed. We'll reach out 30 minutes before Toriano is live.
        </div>
      )}
    </div>
  );
}
